import { ModalDialog } from 'components/ModalDialog';
import { RequestStatus } from 'features/request/requestSlice';
import type { WorkOrderChangeOtherPriceData, WorkOrderChangePriceData, WorkOrderChangePriceItem } from 'gql/graphql';

import { TextField } from '@consta/uikit/TextField';
import { useCallback, useEffect, useState, useMemo } from 'react';

import './editRationale.css';
import { WorkOrderPriceTypeEnum } from '../../WorkOrder';

import { useUpdateOrderChangeOtherPriceRationale } from './hooks/useUpdateOrderChangeOtherPriceRationale';
import { useUpdateOrderChangePriceRationale } from './hooks/useUpdateOrderChangePriceRationale';

interface IWorkOrderChangeOtherPriceDataRow extends WorkOrderChangeOtherPriceData {
    type: string;
}
interface IEditRationaleDialog {
    isOpened: boolean;
    closeModalHandler: () => void;
    refreshTable: (objectIds: number[]) => void;
    data?: WorkOrderChangePriceData | IWorkOrderChangeOtherPriceDataRow | WorkOrderChangePriceItem;
}

export const EditNoteModal: React.FC<IEditRationaleDialog> = ({ isOpened, closeModalHandler, data, refreshTable }) => {
    const isOther = data?.type === WorkOrderPriceTypeEnum.OTHERS;

    const [rationale, setRationale] = useState(data?.rationale || '');

    const {
        uerUpdateOrderChangePriceRationaleStatus,
        updateWorkOrderChangePriceRationaleHandler,
        uerUpdateOrderChangePriceData,
    } = useUpdateOrderChangePriceRationale();

    const {
        uerUpdateOrderChangeOtherPriceRationaleStatus,
        updateOrderChangeOtherPriceRationaleHandler,
        uerUpdateOrderChangeOtherPriceRationaleData,
    } = useUpdateOrderChangeOtherPriceRationale();

    const handleClickOutside = useCallback(() => {
        closeModalHandler();
    }, [closeModalHandler]);

    const handleModalConfirm = useCallback(() => {
        if (isOther) {
            updateOrderChangeOtherPriceRationaleHandler(
                rationale,
                data?.workOrderChangeOtherPriceId,
                data?.workOrderOtherPriceId,
            );
        }

        if (data && 'workOrderChangePriceId' in data) {
            updateWorkOrderChangePriceRationaleHandler(rationale, data?.workOrderChangePriceId, data?.workOrderPriceId);
        }
    }, [
        data,
        isOther,
        rationale,
        updateOrderChangeOtherPriceRationaleHandler,
        updateWorkOrderChangePriceRationaleHandler,
    ]);

    const objectIdsArrEditMutation = useMemo(
        () => (data && 'workOrderObjectId' in data ? [data?.workOrderObjectId] : []),
        [data],
    );

    useEffect(() => {
        if (
            (uerUpdateOrderChangePriceRationaleStatus === RequestStatus.SUCCESS &&
                uerUpdateOrderChangePriceData?.uerUpdateOrderChangePriceRationale.result) ||
            (uerUpdateOrderChangeOtherPriceRationaleData?.uerUpdateOrderChangeOtherPriceRationale.result &&
                uerUpdateOrderChangeOtherPriceRationaleStatus === RequestStatus.SUCCESS)
        ) {
            refreshTable(objectIdsArrEditMutation);
            closeModalHandler();
        }
    }, [
        closeModalHandler,
        objectIdsArrEditMutation,
        refreshTable,
        uerUpdateOrderChangeOtherPriceRationaleData?.uerUpdateOrderChangeOtherPriceRationale.result,
        uerUpdateOrderChangeOtherPriceRationaleStatus,
        uerUpdateOrderChangePriceData?.uerUpdateOrderChangePriceRationale.result,
        uerUpdateOrderChangePriceRationaleStatus,
    ]);

    const onChangeHandler = useCallback(({ value }: { value: string | null }) => {
        setRationale(value ?? '');
    }, []);

    const titleCode = data && 'code' in data ? data?.code : '';

    return (
        <ModalDialog
            title="Редактирование примечания"
            isOpen={isOpened}
            hasOverlay
            withTitleCloseButton
            isCommit={[
                uerUpdateOrderChangePriceRationaleStatus || uerUpdateOrderChangeOtherPriceRationaleStatus,
            ].includes(RequestStatus.FETCHING)}
            onClose={handleClickOutside}
            onConfirm={handleModalConfirm}
            btnConfirmLabel="Сохранить"
            btnCancelLabel="Отменить"
            style={{ overflow: 'hidden' }}
        >
            <div id="EditRationaleDialog">
                <div className="wrapperContent">
                    <p className="title">{titleCode}</p>
                    <p className="subTitle">{data?.name}</p>
                    <TextField
                        name="note"
                        value={rationale}
                        onChange={onChangeHandler}
                        width="full"
                        type="textarea"
                        autoComplete="off"
                        autoCorrect="off"
                        spellCheck="false"
                        autoCapitalize="off"
                        minRows={6}
                    />
                </div>
            </div>
        </ModalDialog>
    );
};

EditNoteModal.displayName = 'EditNoteModal';
